import { useEffect } from "react";
import { Button, Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import StatusCheckBox from "../../../components/Common/StatusCheckbox";
import FieldValidationError from "../../../components/Forms/Element/FieldValidationError";
import InputText from "../../../components/Forms/Element/InputText";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import CategorySelect from "../../../components/Custom/Main/Category/CategorySelect";
import { convertStatusToBoolean } from "../../../../utils/ConverterUtils";
import HelpTip from "../../../components/Common/HelpTip";
import FormSection from "../../../components/Common/Form/FormSection";
import "./PerformancePlan.css";

const INDICATOR_TYPES = [
    "REVENUE", "PRODUCT_TICKET", "RETURN_RATE", "OCCUPANCY_RATE", "NEW_CLIENTS",
    "APPOINTMENT_COUNT", "CATEGORY_REVENUE", "CATEGORY_COUNT", "CATEGORY_TICKET", "CUSTOM"
];

const emptyLevel = (levelOrder) => ({
    name: "",
    levelOrder,
    commissionPercent: 0,
    monthsToMaintain: 2,
    color: "#587388"
});

const emptyIndicator = () => ({
    name: "",
    type: "REVENUE",
    required: true,
    categoryId: null,
    categoryName: null,
    goals: []
});

const PerformancePlanForm = ({ formik, status, setStatus, entityData }) => {

    const { t } = useTranslation();
    const evaluationPeriodOptions = [
        { value: "MONTHLY", label: t('performance.period.MONTHLY') },
        { value: "BIWEEKLY", label: t('performance.period.BIWEEKLY') },
        { value: "WEEKLY", label: t('performance.period.WEEKLY') }
    ];

    const indicatorTypeOptions = INDICATOR_TYPES.map(v => ({
        value: v,
        label: t(`performance.indicator_type.${v}`)
    }));

    useEffect(() => {
        if (entityData != null) {
            formik.setFieldValue('name', entityData.name || "");
            formik.setFieldValue('description', entityData.description || "");
            formik.setFieldValue('evaluationPeriodType', entityData.evaluationPeriodType || "MONTHLY");
            formik.setFieldValue('levels', entityData.levels || []);
            formik.setFieldValue('indicators', entityData.indicators || []);
            setStatus(convertStatusToBoolean(entityData.status));
        } else if (formik.values.levels.length === 0) {
            formik.setFieldValue('levels', [emptyLevel(1)]);
        }
        // eslint-disable-next-line
    }, [entityData]);

    // ─── Levels ──────────────────────────────────────────────────────────────

    const updateLevel = (index, field, value) => {
        const levels = [...formik.values.levels];
        levels[index] = { ...levels[index], [field]: value };
        formik.setFieldValue('levels', levels);
    };

    const addLevel = () => {
        const levels = [...formik.values.levels, emptyLevel(formik.values.levels.length + 1)];
        // add empty goal for each existing indicator
        const indicators = formik.values.indicators.map(ind => ({
            ...ind,
            goals: [...(ind.goals || []), { levelOrder: levels.length, targetValue: 0 }]
        }));
        formik.setFieldValue('levels', levels);
        formik.setFieldValue('indicators', indicators);
    };

    const removeLevel = (index) => {
        const removedOrder = formik.values.levels[index].levelOrder;
        const levels = formik.values.levels.filter((_, i) => i !== index);
        const indicators = formik.values.indicators.map(ind => ({
            ...ind,
            goals: (ind.goals || []).filter(g => g.levelOrder !== removedOrder)
        }));
        formik.setFieldValue('levels', levels);
        formik.setFieldValue('indicators', indicators);
    };

    // ─── Indicators ──────────────────────────────────────────────────────────

    const updateIndicator = (index, field, value) => {
        const indicators = [...formik.values.indicators];
        indicators[index] = { ...indicators[index], [field]: value };
        formik.setFieldValue('indicators', indicators);
    };

    const updateIndicatorFields = (index, fields) => {
        const indicators = [...formik.values.indicators];
        indicators[index] = { ...indicators[index], ...fields };
        formik.setFieldValue('indicators', indicators);
    };

    const addIndicator = () => {
        const ind = emptyIndicator();
        ind.goals = formik.values.levels.map(l => ({ levelOrder: l.levelOrder, targetValue: 0 }));
        formik.setFieldValue('indicators', [...formik.values.indicators, ind]);
    };

    const removeIndicator = (index) => {
        formik.setFieldValue('indicators', formik.values.indicators.filter((_, i) => i !== index));
    };

    const updateGoal = (indIndex, levelOrder, value) => {
        const indicators = [...formik.values.indicators];
        const goals = [...(indicators[indIndex].goals || [])];
        const gIdx = goals.findIndex(g => g.levelOrder === levelOrder);
        if (gIdx >= 0) {
            goals[gIdx] = { ...goals[gIdx], targetValue: value };
        } else {
            goals.push({ levelOrder, targetValue: value });
        }
        indicators[indIndex] = { ...indicators[indIndex], goals };
        formik.setFieldValue('indicators', indicators);
    };

    const getGoalValue = (indicator, levelOrder) => {
        const goal = (indicator.goals || []).find(g => g.levelOrder === levelOrder);
        return goal ? goal.targetValue : 0;
    };

    // Os três indicadores por categoria (faturamento, quantidade e ticket) exigem a categoria.
    const needsCategory = (type) => type === "CATEGORY_REVENUE" || type === "CATEGORY_COUNT" || type === "CATEGORY_TICKET";

    return (
        <div className="form-layout form-layout--spaced">
            {/* ── Informações básicas ── */}
            <FormSection
                title={t('basic_information')}
                description={t('performance.plan_basic_information_hint')}
                icon="fa-solid fa-chart-line"
            >

                <Row className="g-3">
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>{t('name')} <AsteriskRequired /></FormLabel>
                            <InputText name="name" value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                            <FieldValidationError error={formik.errors.name} />
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup>
                            <FormLabel>
                                {t('performance.evaluation_period')}
                                {/* O campo não entra em nenhum cálculo: o dashboard sempre apura o mês
                                    do filtro. A dica avisa para as metas não serem pensadas por semana. */}
                                <HelpTip content={t('performance.evaluation_period_hint')} placement="top" size="sm" />
                            </FormLabel>
                            <CustomSelect
                                name="evaluationPeriodType"
                                value={evaluationPeriodOptions.find(option => option.value === formik.values.evaluationPeriodType)}
                                options={evaluationPeriodOptions}
                                onChange={(option) => formik.setFieldValue('evaluationPeriodType', option?.value || "MONTHLY")}
                                placeholder={t("select")}
                                isClearable={false}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup>
                            <FormLabel>{t('description')}</FormLabel>
                            <InputText name="description" value={formik.values.description} onChange={formik.handleChange} />
                        </FormGroup>
                    </Col>
                </Row>
            </FormSection>

            {/* ── Indicadores ── */}
            <FormSection
                title={t('performance.indicators')}
                description={t('performance.indicators_hint')}
                icon="fa-solid fa-bullseye"
                actions={<Button type="button" variant="primary" size="sm" onClick={addIndicator}>
                        <i className="fa fa-plus me-1"></i>{t('new')}
                    </Button>}
            >

                {formik.values.indicators.length === 0 ? (
                    <p className="text-muted small">{t('performance.no_indicators')}</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th style={{ minWidth: 180 }}>{t('name')} <AsteriskRequired /></th>
                                    <th style={{ minWidth: 160 }}>
                                        <span className="d-flex align-items-center">
                                            {t('performance.indicator_type.label')}
                                            <HelpTip content={t('performance.revenue_hint')} placement="top" size="sm" />
                                        </span>
                                    </th>
                                    <th style={{ minWidth: 80 }}>
                                        <span className="d-flex align-items-center">
                                            {t('performance.required')}
                                            <HelpTip content={t('performance.required_hint')} placement="top" size="sm" />
                                        </span>
                                    </th>
                                    <th style={{ minWidth: 160 }}>{t('performance.category')}</th>
                                    <th style={{ minWidth: 50 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {formik.values.indicators.map((ind, indIdx) => (
                                    <tr key={indIdx}>
                                        <td>
                                            <input className="form-control" value={ind.name}
                                                onChange={(e) => updateIndicator(indIdx, 'name', e.target.value)} />
                                        </td>
                                        <td>
                                            <CustomSelect
                                                value={indicatorTypeOptions.find(o => o.value === ind.type)}
                                                options={indicatorTypeOptions}
                                                onChange={(o) => {
                                                    const type = o?.value;
                                                    updateIndicatorFields(indIdx, {
                                                        type,
                                                        ...(!needsCategory(type) ? { categoryId: null, categoryName: null } : {})
                                                    });
                                                }}
                                                isClearable={false}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                            />
                                        </td>
                                        <td className="text-center">
                                            <input type="checkbox" className="form-check-input"
                                                checked={!!ind.required}
                                                onChange={(e) => updateIndicator(indIdx, 'required', e.target.checked)} />
                                        </td>
                                        <td>
                                            {needsCategory(ind.type) ? (
                                                <CategorySelect
                                                    value={ind.categoryId ? { value: ind.categoryId, label: ind.categoryName } : null}
                                                    onChange={(o) => {
                                                        updateIndicatorFields(indIdx, {
                                                            categoryId: o?.value ?? null,
                                                            categoryName: o?.label ?? null
                                                        });
                                                    }}
                                                    isClearable
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                />
                                            ) : (
                                                <span className="text-muted small">—</span>
                                            )}
                                        </td>
                                        <td>
                                            <Button type="button" variant="danger" size="sm" onClick={() => removeIndicator(indIdx)}>
                                                <i className="fa fa-trash"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </FormSection>

            {/* ── Níveis ── */}
            <FormSection
                title={t('performance.levels')}
                description={t('performance.levels_hint')}
                icon="fa-solid fa-layer-group"
                actions={<Button type="button" variant="primary" onClick={addLevel}>
                        <i className="fa fa-plus me-2"></i>{t('performance.add_level')}
                    </Button>}
            >

                <div className="row g-4">
                    {formik.values.levels.map((level, index) => (
                        <div key={index} className="col-xl-4 col-md-6">
                            <div className="level-card">
                                {/* Card header */}
                                <div className="level-card__header" style={level.color ? { background: `linear-gradient(135deg, ${level.color} 0%, ${level.color}cc 100%)` } : {}}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="level-card__badge">
                                            #{level.levelOrder}
                                        </div>
                                        <div className="level-card__color-picker-wrapper">
                                            <input 
                                                type="color" 
                                                id={`color-picker-${index}`}
                                                className="level-card__color-input-hidden" 
                                                value={level.color || "#587388"}
                                                onChange={(e) => updateLevel(index, 'color', e.target.value)}
                                            />
                                            <label htmlFor={`color-picker-${index}`} className="level-card__color-trigger" title={t('performance.choose_level_color')}>
                                                <i className="fas fa-palette"></i>
                                            </label>
                                        </div>
                                        <h6>{level.name || `${t('performance.level')} ${level.levelOrder}`}</h6>
                                    </div>
                                    <button type="button" className="level-card__remove-btn" onClick={() => removeLevel(index)}>
                                        <i className="fa fa-times"></i>
                                    </button>
                                </div>

                                {/* Card body */}
                                <div className="level-card__body">
                                    <div className="level-card__field-group">
                                        <label className="level-card__label">
                                            <i className="fa fa-tag"></i> {t('name')}
                                        </label>
                                        <input 
                                            className="form-control level-card__input" 
                                            placeholder={t('performance.level_name_placeholder')}
                                            value={level.name}
                                            onChange={(e) => updateLevel(index, 'name', e.target.value)} 
                                        />
                                    </div>

                                    <Row className="g-3">
                                        <Col xs={6}>
                                            <div className="level-card__field-group">
                                                <label className="level-card__label">
                                                    <i className="fa fa-percent"></i> {t('performance.commission_short')}
                                                </label>
                                                <input 
                                                    className="form-control level-card__input" 
                                                    type="number" 
                                                    step="0.01" 
                                                    value={level.commissionPercent}
                                                    onChange={(e) => updateLevel(index, 'commissionPercent', Number(e.target.value))} 
                                                />
                                            </div>
                                        </Col>
                                        <Col xs={6}>
                                            <div className="level-card__field-group">
                                                <label className="level-card__label">
                                                    <i className="fa fa-calendar-check"></i> {t('performance.months_short')}
                                                </label>
                                                <input 
                                                    className="form-control level-card__input" 
                                                    type="number" 
                                                    min="1"
                                                    value={level.monthsToMaintain}
                                                    onChange={(e) => updateLevel(index, 'monthsToMaintain', Number(e.target.value))} 
                                                />
                                                <small className="text-muted">{t('performance.maintenance_period_hint')}</small>
                                            </div>
                                        </Col>
                                    </Row>

                                    {formik.values.indicators.length > 0 && (
                                        <div className="level-card__goals-section">
                                            <label className="level-card__label mb-3">
                                                <i className="fa fa-bullseye"></i> {t('performance.goals')}
                                            </label>
                                            <div className="d-flex flex-column gap-2">
                                                {formik.values.indicators.map((ind, indIdx) => (
                                                    <div key={indIdx} className="level-card__goal-item">
                                                        <span className="level-card__goal-name">
                                                            {ind.name || `#${indIdx + 1}`}
                                                        </span>
                                                        <input
                                                            className="level-card__goal-input"
                                                            type="number"
                                                            step="0.01"
                                                            value={getGoalValue(ind, level.levelOrder)}
                                                            onChange={(e) => updateGoal(indIdx, level.levelOrder, Number(e.target.value))}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <FieldValidationError error={formik.errors.levels} />
            </FormSection>

            {/* ── Status ── */}
            <FormSection
                title={t('status')}
                description={t('performance.plan_status_hint')}
                icon="fa-solid fa-toggle-on"
                compact
            >

                <Row>
                    <Col md={12}>
                        <FormGroup className="mb-0">
                            <StatusCheckBox
                                name="status"
                                defaultChecked={status}
                                checked={status}
                                onClick={() => setStatus(!status)}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            </FormSection>
        </div>
    );
}


export default PerformancePlanForm;
